import { createLazyComponent, createLoadingComponent, preloadComponent } from '../../utils/lazyLoading';

// Consolidated loading components by user type
const CarrierLoading = createLoadingComponent('Carrier Dashboard');
const ShipperLoading = createLoadingComponent('Shipper Dashboard');
const BrokerLoading = createLoadingComponent('Broker Dashboard');
const AdminLoading = createLoadingComponent('Admin Panel');
const AuthLoading = createLoadingComponent('Authentication');
const PublicLoading = createLoadingComponent('Loading...');

// ============================================================================
// CONSOLIDATED CHUNKS BY USER TYPE (Reduces 100+ chunks to ~10 chunks)
// ============================================================================

// CARRIER CHUNK - All carrier pages in one chunk
export const LazyCarrierPages = createLazyComponent(
  () => import('../../pages/carrier/CarrierPages'),
  { fallback: CarrierLoading }
);

// SHIPPER CHUNK - All shipper pages in one chunk  
export const LazyShipperPages = createLazyComponent(
  () => import('../../pages/shipper/ShipperPages'),
  { fallback: ShipperLoading }
);

// BROKER CHUNK - All broker pages in one chunk
export const LazyBrokerPages = createLazyComponent(
  () => import('../../pages/broker/BrokerPages'),
  { fallback: BrokerLoading }
);

// ADMIN CHUNK - All admin pages in one chunk
export const LazyAdminPages = createLazyComponent(
  () => import('../../pages/admin/AdminPages'),
  { fallback: AdminLoading }
);

// AUTH CHUNK - All authentication pages in one chunk
export const LazyAuthPages = createLazyComponent(
  () => import('../../pages/auth/AuthPages'),
  { fallback: AuthLoading }
);

// PUBLIC CHUNK - All public pages in one chunk
export const LazyPublicPages = createLazyComponent(
  () => import('../../pages/public/PublicPages'),
  { fallback: PublicLoading }
);

// ============================================================================
// HEAVY COMPONENTS (Keep separate for optimal loading)
// ============================================================================

export const LazyMapboxMap = createLazyComponent(
  () => import('../../components/common/MapboxMap'),
  { fallback: createLoadingComponent('Map Loading...') }
);

export const LazyAdvancedPerformanceDashboard = createLazyComponent(
  () => import('../../components/admin/AdvancedPerformanceDashboard'),
  { fallback: AdminLoading }
);

export const LazySystemHealthMonitor = createLazyComponent(
  () => import('../../components/admin/SystemHealthMonitor'),
  { fallback: AdminLoading }
);

export const LazyAvailabilityDashboard = createLazyComponent(
  () => import('../../components/admin/AvailabilityDashboard'),
  { fallback: AdminLoading }
);

export const LazyProcessingIntegrityDashboard = createLazyComponent(
  () => import('../../components/admin/ProcessingIntegrityDashboard'),
  { fallback: AdminLoading }
);

export const LazySecurityComplianceDashboard = createLazyComponent(
  () => import('../../components/admin/SecurityComplianceDashboard'),
  { fallback: AdminLoading }
);

export const LazyUnifiedSOC2ComplianceDashboard = createLazyComponent(
  () => import('../../components/admin/UnifiedSOC2ComplianceDashboard'),
  { fallback: AdminLoading }
);

// ============================================================================
// LEGACY COMPATIBILITY (Maintain existing imports)
// ============================================================================

// Carrier Routes - Individual components
export const LazyCarrierDashboard = createLazyComponent(
  () => import('../../pages/carrier/HomeFeed'),
  { fallback: CarrierLoading }
);
export const LazyAvailableLoads = createLazyComponent(
  () => import('../../pages/carrier/AvailableLoads'),
  { fallback: CarrierLoading }
);
export const LazyMyLoads = createLazyComponent(
  () => import('../../pages/carrier/MyLoads'),
  { fallback: CarrierLoading }
);
export const LazyLoadDetails = createLazyComponent(
  () => import('../../pages/carrier/LoadDetails'),
  { fallback: CarrierLoading }
);
export const LazyHomeFeed = createLazyComponent(
  () => import('../../pages/carrier/HomeFeed'),
  { fallback: CarrierLoading }
);
export const LazyCarrierProfile = createLazyComponent(
  () => import('../../pages/carrier/Profile'),
  { fallback: CarrierLoading }
);
export const LazyCarrierMyProfile = createLazyComponent(
  () => import('../../pages/carrier/MyProfile'),
  { fallback: CarrierLoading }
);
export const LazyCarrierSettings = createLazyComponent(
  () => import('../../pages/carrier/Settings'),
  { fallback: CarrierLoading }
);
export const LazyCarrierPayments = createLazyComponent(
  () => import('../../pages/carrier/Payments'),
  { fallback: CarrierLoading }
);
export const LazyCarrierPartners = createLazyComponent(
  () => import('../../pages/carrier/Partners'),
  { fallback: CarrierLoading }
);
export const LazyOnboardingWalkthrough = createLazyComponent(
  () => import('../../pages/carrier/OnboardingWalkthrough'),
  { fallback: CarrierLoading }
);
export const LazyBrokerOnboarding = createLazyComponent(
  () => import('../../pages/broker/BrokerOnboardingPage'),
  { fallback: BrokerLoading }
);
export const LazyShipperOnboarding = createLazyComponent(
  () => import('../../pages/shipper/ShipperOnboardingPage'),
  { fallback: ShipperLoading }
);
export const LazyBrokerDetails = createLazyComponent(
  () => import('../../pages/carrier/BrokerDetails'),
  { fallback: CarrierLoading }
);
export const LazyShipperDetails = createLazyComponent(
  () => import('../../pages/carrier/ShipperDetails'),
  { fallback: CarrierLoading }
);

/** Same dynamic imports as lazy routes — browser caches the chunk for instant navigate after warm. */
export type CarrierPrefetchKey =
  | 'home'
  | 'available-loads'
  | 'my-loads'
  | 'load-details'
  | 'profile'
  | 'payments'
  | 'partners'
  | 'settings';

const carrierChunkImporters: Record<CarrierPrefetchKey, () => Promise<unknown>> = {
  home: () => import('../../pages/carrier/HomeFeed'),
  'available-loads': () => import('../../pages/carrier/AvailableLoads'),
  'my-loads': () => import('../../pages/carrier/MyLoads'),
  'load-details': () => import('../../pages/carrier/LoadDetails'),
  profile: () => import('../../pages/carrier/Profile'),
  payments: () => import('../../pages/carrier/Payments'),
  partners: () => import('../../pages/carrier/Partners'),
  settings: () => import('../../pages/carrier/Settings'),
};

export function prefetchCarrierChunk(key: CarrierPrefetchKey): void {
  preloadComponent(carrierChunkImporters[key]);
}

export const LazyBrokerProfileSetup = createLazyComponent(
  () => import('../../pages/broker/ProfileSetup'),
  { fallback: BrokerLoading }
);
export const LazyBrokerProfileView = createLazyComponent(
  () => import('../../pages/broker/ProfileView'),
  { fallback: BrokerLoading }
);
export const LazyShipperProfileSetup = createLazyComponent(
  () => import('../../pages/shipper/ProfileSetup'),
  { fallback: ShipperLoading }
);
export const LazyShipperProfileView = createLazyComponent(
  () => import('../../pages/shipper/ProfileView'),
  { fallback: ShipperLoading }
);

// Shipper Routes - All point to consolidated chunk
export const LazyShipperDashboard = LazyShipperPages;
export const LazyPurchaseOrders = LazyShipperPages;
export const LazyTestPurchaseOrder = LazyShipperPages;
export const LazyCreateShipment = LazyShipperPages;
export const LazyShipperCarrierPartners = LazyShipperPages;
export const LazyShippingSchedule = LazyShipperPages;
export const LazyShipperProfile = LazyShipperPages;
export const LazyShipperSettings = LazyShipperPages;

// Broker Routes - Individual components
export const LazyBrokerDashboard = createLazyComponent(
  () => import('../../pages/broker/Dashboard'),
  { fallback: BrokerLoading }
);
export const LazyBrokerPurchaseOrders = createLazyComponent(
  () => import('../../pages/broker/PurchaseOrders'),
  { fallback: BrokerLoading }
);
export const LazyBrokerCreatePO = createLazyComponent(
  () => import('../../pages/broker/CreatePO'),
  { fallback: BrokerLoading }
);
export const LazyBrokerCarrierPartners = createLazyComponent(
  () => import('../../pages/broker/CarrierPartners'),
  { fallback: BrokerLoading }
);
export const LazyBrokerCarrierDetails = createLazyComponent(
  () => import('../../pages/broker/CarrierDetails'),
  { fallback: BrokerLoading }
);
export const LazyBrokerShippingSchedule = createLazyComponent(
  () => import('../../pages/broker/ShippingSchedule'),
  { fallback: BrokerLoading }
);
export const LazyBrokerDriverUpdates = createLazyComponent(
  () => import('../../pages/broker/DriverUpdates'),
  { fallback: BrokerLoading }
);
export const LazyBrokerPayInvoices = createLazyComponent(
  () => import('../../pages/broker/PayInvoices'),
  { fallback: BrokerLoading }
);
export const LazyBrokerShipmentArchive = createLazyComponent(
  () => import('../../pages/broker/ShipmentArchive'),
  { fallback: BrokerLoading }
);
export const LazyBrokerCarrierDirectory = createLazyComponent(
  () => import('../../pages/broker/CarrierDirectory'),
  { fallback: BrokerLoading }
);
export const LazyBrokerSettings = createLazyComponent(
  () => import('../../pages/broker/Settings'),
  { fallback: BrokerLoading }
);
export const LazyBrokerProfile = createLazyComponent(
  () => import('../../pages/broker/Profile'),
  { fallback: BrokerLoading }
);
export const LazyBrokerMultiLoadCarrierAssignment = createLazyComponent(
  () => import('../../pages/broker/MultiLoadCarrierAssignment'),
  { fallback: BrokerLoading }
);

// Admin Routes - All point to consolidated chunk
export const LazyAdminDashboard = LazyAdminPages;
export const LazyAdminSetup = LazyAdminPages;
export const LazyAdminManagement = LazyAdminPages;
export const LazyAdminAccess = LazyAdminPages;
export const LazyMonitoringDashboard = LazyAdminPages;
export const LazyFeatureFlagManager = LazyAdminPages;
export const LazyCacheManager = LazyAdminPages;
export const LazyRateLimitManager = LazyAdminPages;
export const LazySubscriptionTierManager = LazyAdminPages;

/** Admin tools hub (separate entry; same pattern as cancellation/ACI). */
export const LazyAdminToolsHub = createLazyComponent(
  () => import('../../pages/admin/AdminToolsHub'),
  { fallback: AdminLoading }
);

/** Phase 5: super-admin account cancellation UI (separate entry to avoid AdminPages nested path issues). */
export const LazySuperAdminAccountCancellation = createLazyComponent(
  () => import('../../pages/admin/SuperAdminAccountCancellation'),
  { fallback: AdminLoading }
);

/** Phase 2: super-admin ACI status + legal (separate chunk). */
export const LazyAuthorityContinuityIntelligenceDashboard = createLazyComponent(
  () => import('../../pages/admin/AuthorityContinuityIntelligenceDashboard'),
  { fallback: AdminLoading }
);

/** Failover: super-admin read-only failover status (separate chunk). */
export const LazyFailoverStatus = createLazyComponent(
  () => import('../../pages/admin/FailoverStatus'),
  { fallback: AdminLoading }
);

// Auth Routes - All point to consolidated chunk
export const LazyLogin = LazyAuthPages;
export const LazyRegister = LazyAuthPages;
export const LazyBrokerRegister = LazyAuthPages;

// Public Routes - All point to consolidated chunk
export const LazyLandingPage = LazyPublicPages;
export const LazyTechnologyPage = LazyPublicPages;
export const LazyAboutPage = LazyPublicPages;
export const LazyOAuthCallback = LazyPublicPages;
export const LazyViewProfile = LazyPublicPages;
export const LazyFeatureFlagTest = LazyPublicPages;

